import React from "react";
import { RouteComponentProps } from "react-router";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
  Row,
  Col,
  Button,
  Modal,
  message,
  Spin,
  List,
  Switch,
  Divider,
} from "antd";
import Card from "../components/Card";
import {
  Role,
  User,
  Permission,
  APIError
} from "../helpers/shared-interfaces";
import { request, handleValidationErrors } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import EntitledSection from "../components/EntitledSection";
import allEntitlements from "../config/entitlements";
import Search from "antd/lib/input/Search";
import { DELETE_ROLE, GET_ROLE_WITH_ENTITLEMENTS, UPDATE_ROLE } from "../gql/roles";
import { ACTIVATE_PERMISSION, DEACTIVATE_PERMISSION, GET_PERMISSIONS } from "../gql/permissions";
import { FormInstance ,} from "antd/lib/form";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import * as Sentry from "@sentry/browser";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  name: string;
  isLoading: boolean;
  hasChanged: boolean;
  id: string;
  permissionCurrentlyBeingUpdated: string;
  showDialog: boolean;
  users: User[] | [];
  permissions: Permission[] | [];
  filterBy: string;
  isFiltering: boolean;
  filteredPermissions: Permission[];
  showUpdateRoleModal: boolean;
  isUpdatingRole: boolean;
}

interface RouteParams {
  id: string;
}

export function renderListItem(
  handlePermissionChange: (
    permissionId: string,
  ) => (checked: boolean) => void,
  permissionCurrentlyBeingUpdated: string,
): (item: Permission) => JSX.Element {
  return function ListItemCreator(item: Permission): JSX.Element {
    return (
      <List.Item>
        <Row
          style={{
            width: "100%",
          }}
        >
          <Col span={14}>{item.entitlement.name}</Col>
          <Col
            style={{
              textAlign: "right",
            }}
            span={10}
          >
            <Spin
              spinning={permissionCurrentlyBeingUpdated === item.id}
              style={{
                position: "relative",
                top: "11px",
              }}
            >
              <Switch
                onChange={handlePermissionChange(item.id)}
                defaultChecked={item.isActive}
                key={item.id}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </Spin>
          </Col>
        </Row>
      </List.Item>
    );
  };
}

export function injectKeyProp(data: Role[]): Role[] {
  const tempData: Role[] = data.map((role: Role): Role => {
    return { ...role, key: role.id };
  });
  return tempData;
}

function ModalWrapper(
  showDialog: boolean,
  deleteRole: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteRole}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this role?</div>
    </Modal>
  );
}


export default class ViewRole extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: React.RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      permissionCurrentlyBeingUpdated: "",
      permissions: [],
      users: [],
      isLoading: true,
      id: "",
      name: "",
      hasChanged: false,
      showDialog: false,
      isFiltering: false,
      filterBy: "",
      filteredPermissions: [],
      showUpdateRoleModal: false,
      isUpdatingRole: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.insertPermissionIfDoesntExist =
      this.insertPermissionIfDoesntExist.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getRoleData = this.getRoleData.bind(this);
    this.getPermissions = this.getPermissions.bind(this);

    this.handlePermissionChange = this.handlePermissionChange.bind(this);
    this.deleteRole = this.deleteRole.bind(this);
    this.filterPermissions = this.filterPermissions.bind(this);
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllRolesPage = (): void => {
    this.props.history.push("/roles");
  };

  getPermissions(): void {
    const { id: roleId } = this.state;

    this.setState({ isLoading: true });

    request({
      query: GET_PERMISSIONS,
      variables: { roleId },
    })
      .then(({ data }: AxiosResponse<any>): void => {
        if (!data.errors) {
          this.setState({
            permissions: data.data.permissions,
          });
        } else {
          message.error("Failed to get permissions");
        }
      })
      .catch((error) => {
        console.error("Permission fetch error:", error);
        message.error("An error occurred while fetching permissions");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  deleteRole(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_ROLE,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteRole.result) {
              message.success("Role deleted successfuly");
              this.redirectToAllRolesPage();
            } else {
              message.error("Failed to delete role");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  async getRoleData(): Promise<void> {
    const { id: roleId } = this.state;

    this.setState({ isLoading: true });

    try {
      const [roleResponse, permissionsResponse] = await Promise.all([
        request({
          query: GET_ROLE_WITH_ENTITLEMENTS,
          variables: { id: roleId },
        }),
        request({
          query: GET_PERMISSIONS,
          variables: { roleId },
        }),
      ]);

      const roleData = roleResponse.data.data.role;
      const permissions: Permission[] = permissionsResponse.data.data.permissions;

      if (!roleResponse.data.errors && !permissionsResponse.data.errors) {
        this.setState({
          id: roleData?.id,
          name: roleData?.name,
          users: roleData?.users,
          permissions,
        });
      } else {
        message.error("Failed to get role data or permissions.");
      }
    } catch (error) {
      message.error(`An error occurred while loading data. - ${generateDisplayErrorMessage(error)}`);
      console.error(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  handleInputChange(
    e: React.ChangeEvent<{
      value: string;
    }>,
  ): void {
    this.setState({ name: e.target.value });
  }

  toggleUpdateRoleModal = (): void => {
    this.setState(
      (prevState) => ({
        showUpdateRoleModal: !prevState.showUpdateRoleModal,
      }),
      () => {
        if (this.state.showUpdateRoleModal) {
        } else {
          this.reference.current?.resetFields();
        }
      },
    );
  };

  async filterPermissions(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const filterBy: string = e.target.value;
    this.setState({ filterBy, isFiltering: filterBy.length > 0 });

    if (filterBy.length === 0) {
      this.setState({ isFiltering: false, filteredPermissions: [] });
      return;
    }

    const filtered: Permission[] = this.state.permissions.filter(
      (permission: Permission): boolean => {
        return permission.entitlement.name.toLowerCase().includes(filterBy.toLowerCase());
      }
    );

    this.setState({ filteredPermissions: filtered });
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getRoleData();
    });
  }

  async insertPermissionIfDoesntExist(permission: Permission): Promise<void> {
    const { permissions } = this.state;
    const exists: Permission | undefined = permissions.find(
      (p: Permission): boolean => {
        return p.id === permission.id;
      },
    );

    if (!exists) {
      const newPermissions: Permission[] = [...permissions, permission];
      this.setState({ permissions: newPermissions });
    }
  }

  async activatePermission(id: string): Promise<any> {
    try {
      const response = await request({
        query: ACTIVATE_PERMISSION,
        variables: { id },
      });

      if (!response.data.data.activatePermission) {
        return null;
      }

      const updatedPermission = response.data.data.activatePermission.permission;

      if (updatedPermission) {
        this.setState((prevState) => ({
          permissions: (prevState.permissions as Permission[]).map((perm) =>
            perm.id === updatedPermission.id ? updatedPermission : perm
          ),
        }));
        return response;
      } else {
        message.error("Failed to activate permission.");
        return null;
      }
    } catch (error) {
      console.error(error);
      message.error(`An error occurred while activating permission. - ${generateDisplayErrorMessage(error)}`);
      return null;
    }
  }

  async deActivatePermission(id: string): Promise<void> {
    try {
      const response = await request({
        query: DEACTIVATE_PERMISSION,
        variables: { id },
      });

      const updatedPermission = response.data.data.deactivatePermission.permission;

      if (updatedPermission) {
        this.setState((prevState) => ({
          permissions: (prevState.permissions as Permission[]).map((perm) =>
            perm.id === updatedPermission.id ? updatedPermission : perm
          ),
        }));
      } else {
        message.error("Failed to deactivate permission.");
      }
    } catch (error) {
      console.error(error);
      message.error(`An error occurred while deactivating permission. - ${generateDisplayErrorMessage(error)}`);
    }
  }
  
  updateRole = async (): Promise<void> => {
      interface Response {
        data?: {
          updateRole?: {
            role?: {
              name: string;
            };
            errors?: APIError[];
          };
        };
        errors?: Record<string, unknown>[];
      }
  
      if (!this.reference.current) return;

      let data;
  
      try {
        data = await this.reference.current.validateFields(["name"]);
      } catch (error) {
        handleValidationErrors(error);
        return;
      }
  
      try {
        const response: AxiosResponse<Response> = await request({
          query: UPDATE_ROLE,
          variables: { name: data.name, id: this.state.id },
        });
  
        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.updateRole &&
          response.data.data.updateRole.role
        ) {
          message.success("Role update sucessfully");
          this.setState(
          {
            showUpdateRoleModal: false,
          },
          this.getRoleData,
        );
        } else {
          message.error("Failed to update role");
        }
      } catch (error) {
        message.error(`Failed to update role - ${generateDisplayErrorMessage(error)}`);
        Sentry.captureException(error);
      }
      finally {
        this.setState({ isUpdatingRole: false });
      }
    };
   
  handlePermissionChange(id: string): (checked: boolean) => Promise<void> {
    return async (checked): Promise<void> => {
      const { permissions } = this.state;

      this.setState({ permissionCurrentlyBeingUpdated: id });

      try {
        if (checked) {
          const response: any = await this.activatePermission(id);
          const permission = response.data.data.activatePermission.permission;

          if (permission) {
            await this.insertPermissionIfDoesntExist(permission);
          } else {
            console.error("Permission not found in response");
          }
        } else {
          const permission = permissions.find((p: Permission) => p.id === id);

          if (permission) {
            await this.deActivatePermission(permission.id);
          }
        }
      } catch (error) {
        console.error("Failed to update permission", error);
      } finally {
        this.setState({ permissionCurrentlyBeingUpdated: "" });
      }
    };
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      name,
      showDialog,
      users,
      permissionCurrentlyBeingUpdated,
      filterBy,
      isFiltering,
      filteredPermissions,
      permissions,
      showUpdateRoleModal,
      isUpdatingRole, 
    } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            {ModalWrapper(
              showDialog,
              this.deleteRole,
              this.toggleDeleteConfirmationModal,
            )}
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={8}
              >
                <div>Role Name</div>
                <div>
                  <h1
                    style={{
                      fontWeight: "bold",
                      fontSize: "25px",
                      marginBottom: "0px",
                    }}
                    data-cy="test-role-name"
                  >
                    {name?.toUpperCase()}
                  </h1>
                </div>
              </Col>
              <Col
                style={{
                  textAlign: "left",
                }}
                span={6}
              >
                <div>User Count</div>
                <div>
                  <h1
                    style={{
                      fontWeight: "bold",
                      fontSize: "25px",
                      marginBottom: "0px",
                    }}
                  >
                    {users?.length}
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                  position: "relative",
                  top: "12px",
                }}
                span={10}
              >
                <EntitledSection entitlement={allEntitlements.Roles.edit}>
                    <Button
                      icon={<EditOutlined />}
                      size="large"
                      onClick={this.toggleUpdateRoleModal}
                      style={{
                        backgroundColor: "  rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "10px",
                      }}
                      data-cy="test-edit-role-button"
                    >
                      Edit
                    </Button>
                </EntitledSection>
                {users?.length === 0 && (
                  <EntitledSection entitlement={allEntitlements.Roles.delete}>
                    <Button
                      icon={<DeleteOutlined />}
                      size="large"
                      onClick={this.toggleDeleteConfirmationModal}
                    >
                      Delete
                    </Button>
                  </EntitledSection>
                )}
              </Col>
            </Row>

            <Divider></Divider>

            <Row>
              <Col span={10}>
                <div
                  style={{
                    marginTop: "10px",
                  }}
                >
                  <h1>Permissions</h1>
                </div>
              </Col>
              <Col span={10} offset={4} style={{ textAlign: "right" }}>
                <Search
                  placeholder="Filter by...."
                  size="large"
                  style={{
                    width: "100%",
                    marginBottom: "24px",
                  }}
                  onChange={this.filterPermissions}
                  value={filterBy}
                  data-testid="filter-input"
                  allowClear
                  id="search-input"
                />
              </Col>
            </Row>

            <Row>
              <List
                style={{ width: "100%" }}
                size="large"
                header={null}
                footer={null}
                bordered
                dataSource={isFiltering ? filteredPermissions : permissions}
                renderItem={renderListItem(
                  this.handlePermissionChange,
                  permissionCurrentlyBeingUpdated,
                )}
              />
            </Row>
            {showUpdateRoleModal &&
              ModalCreator({
                title: "Update Role",
                okText: "Update",
                okType: "primary",
                showModal: showUpdateRoleModal,
                onOkay: this.updateRole,
                toggleModal: this.toggleUpdateRoleModal,
                inscription: (
                  <Spin spinning={isUpdatingRole}>
                    <BaseForm
                      layout="vertical"
                      reference={this.reference}
                      fieldsToHide={[]}
                      fields={[
                        {
                          name: "name",
                          label: "Name",
                          initialValue: name,
                          inputType: "string",
                          placeholder: "Enter name",
                          required: false,
                          colConfig: { span: 22 },
                        },
                      ]}
                    />
                  </Spin>
                ),
              })}
          </div>
        </Card>
      </Spin>
    );
  }
}
